// deps
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// internal-deps
import {
    ADDRESS_NUDGE_TOOLTIP_STATUS,
    AddressInfo,
    GlobalOverlayPriority,
    HalfcardIds,
    OverlayType,
    ReviewedCartApiResponse,
    UserAddress,
    MROReviewedCartApiResponse,
} from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
// contsxts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import { isAwayLocationWithoutOFO } from "@cart-client/helpers/Cart";
import { getAllDeliveryValidAddresses, getSelectedAddress } from "@cart-client/helpers/CartFooter";
import { openEditNumberBottomSheet } from "@cart-client/helpers/OFO";
import { isOpenedAddressFromQuickCheckout } from "@cart-client/helpers/QuickCheckoutCart";

// constants
import { DELIVERY_ADDRESS_ID, SOCIAL_OFO_TEXT } from "@cart-client/constants/TextDict";

// hooks
import useFetchAddressList from "./useFetchAddressList";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { useCartAddressNudgeAnalytics } from "./useCartAddressNudgeAnalytics";
import { CartOverlaysContext, CheckoutActionTypes, CheckoutContext } from "@swiggy-private/checkout-core";

interface useFetchAddressOnClickReturnType {
    goToAddAddress: ({
        selectedAddress,
        addressList,
        source,
    }: {
        selectedAddress?: AddressInfo | null;
        addressList?: AddressInfo[];
        source?: string;
    }) => void;
    onSelectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => Promise<void>;
    goToSelectAddress: () => void;
    onAddressListClose: () => void;
    selectedAddress: UserAddress | null;
    displayAddressList: AddressInfo[] | UserAddress[];
}

export const useFetchAddressOnClick = (
    cartTotal: number | undefined,
    addresses: ReviewedCartApiResponse["addresses"] | MROReviewedCartApiResponse["addresses"] | undefined,
    isCartStatusSuccess?: boolean,
): useFetchAddressOnClickReturnType => {
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { localCart } = cartState;
    const { cartAddressState, setCartAddressState } = useContext(CartAddressContext);
    const { addressList } = cartAddressState;
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { registerOverlays, enabledOverlayId, onCloseOverlayImmediate } = useContext(CartOverlaysContext);

    // QC → MRO address flow: stay on full cart after select; clear flag on select/close.
    const openedAddressFromQuickCheckout = isOpenedAddressFromQuickCheckout(checkoutState);
    const hasRegisteredAddressOverlayRef = useRef(false);
    const pendingUnregisterAddressOverlayRef = useRef(false);

    const { isCartAddressSelectorDisabled, fetchAddressList, addNewAddress } = useFetchAddressList();
    const { onClickSelectAddress, onClickChangeAddress, onClickAddressChangeBack, onClickAddNewAddress } =
        useCartAddressNudgeAnalytics();

    useEffect(() => {
        if (!openedAddressFromQuickCheckout || hasRegisteredAddressOverlayRef.current) {
            return;
        }
        registerOverlays({
            overlayType: OverlayType.HALFCARD,
            priority: GlobalOverlayPriority.ADDRESS_LIST,
            id: HalfcardIds.ADDRESS_LIST,
        });
        hasRegisteredAddressOverlayRef.current = true;
    }, [openedAddressFromQuickCheckout, registerOverlays]);

    // Close once our overlay becomes enabled if the user already dismissed the address list.
    useEffect(() => {
        if (!pendingUnregisterAddressOverlayRef.current || enabledOverlayId !== HalfcardIds.ADDRESS_LIST) {
            return;
        }
        onCloseOverlayImmediate();
        pendingUnregisterAddressOverlayRef.current = false;
        hasRegisteredAddressOverlayRef.current = false;
    }, [enabledOverlayId, onCloseOverlayImmediate]);

    const clearOpenedAddressFromQuickCheckout = useCallback(() => {
        if (!openedAddressFromQuickCheckout) {
            return;
        }
        if (hasRegisteredAddressOverlayRef.current) {
            if (enabledOverlayId === HalfcardIds.ADDRESS_LIST) {
                onCloseOverlayImmediate();
                hasRegisteredAddressOverlayRef.current = false;
            } else {
                pendingUnregisterAddressOverlayRef.current = true;
            }
        }
        setCheckoutState({
            type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
            payload: { ...checkoutState.payload, openedAddressFromQuickCheckout: false },
        });
    }, [
        checkoutState.payload,
        enabledOverlayId,
        onCloseOverlayImmediate,
        openedAddressFromQuickCheckout,
        setCheckoutState,
    ]);

    const openAddReceiverDetailsSheet = useCallback(
        (newAddress: UserAddress) => {
            openEditNumberBottomSheet(newAddress, SOCIAL_OFO_TEXT.changeAction).then(() => {
                doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: newAddress.id,
                });
            });
        },
        [doUpdateCart, localCart],
    );

    const hideNudgeAndCloseBottomSheet = useCallback(() => {
        setCartAddressState((prevCartAddressState) => ({
            ...prevCartAddressState,
            showAddressList: false,
            addressNudgeConfig: {
                ...prevCartAddressState.addressNudgeConfig,
                showAddressNudgeIcon: false,
                addressNudgeTooltipStatus:
                    prevCartAddressState.addressNudgeConfig.addressNudgeTooltipStatus ===
                    ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW
                        ? ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN
                        : prevCartAddressState.addressNudgeConfig.addressNudgeTooltipStatus,
            },
        }));
    }, [setCartAddressState]);

    const onSelectAddress = useCallback(
        async (selectedAddress: AddressInfo, addressList: AddressInfo[]) => {
            onClickSelectAddress(selectedAddress, addressList);

            // on address change hide the bottom sheet, if addressNudgeTootlip is showing close it and remove addressNudgeIcon
            hideNudgeAndCloseBottomSheet();

            try {
                document.cookie = `${DELIVERY_ADDRESS_ID}=${selectedAddress.id}`;
            } catch (error) {
                console.error("Failed to set delivery address cookie:", error);
            }

            if (!updateCartInProgress) {
                const data = await doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: selectedAddress.id,
                });
                // QC opened address on full cart — stay here; clear origin so later opens don't assume QC.
                clearOpenedAddressFromQuickCheckout();
                if (data && isAwayLocationWithoutOFO(data)) {
                    let newSelectedAddress: UserAddress | null = null;
                    data?.data?.addresses?.filter((address: UserAddress) => {
                        if (address.selected) {
                            newSelectedAddress = address;
                        }
                    });
                    newSelectedAddress && openAddReceiverDetailsSheet(newSelectedAddress);
                }
            }
        },
        [
            onClickSelectAddress,
            hideNudgeAndCloseBottomSheet,
            updateCartInProgress,
            doUpdateCart,
            localCart,
            openAddReceiverDetailsSheet,
            clearOpenedAddressFromQuickCheckout,
        ],
    );

    const selectedAddress = useMemo(() => getSelectedAddress(addresses), [addresses]);

    const goToSelectAddress = useCallback(() => {
        onClickChangeAddress(isCartStatusSuccess, cartTotal);

        // if XP is off or address list is available
        if (!isCartAddressSelectorDisabled || !isEmpty(addressList)) {
            setCartAddressState((prevCartAddressState) => ({
                ...prevCartAddressState,
                showAddressList: true,
            }));
            return;
        }
        fetchAddressList();
    }, [
        onClickChangeAddress,
        cartTotal,
        isCartStatusSuccess,
        isCartAddressSelectorDisabled,
        addressList,
        fetchAddressList,
        setCartAddressState,
    ]);

    const onAddressListClose = useCallback(() => {
        onClickAddressChangeBack();
        setCartAddressState({
            ...cartAddressState,
            showAddressList: false,
        });
        clearOpenedAddressFromQuickCheckout();
    }, [cartAddressState, clearOpenedAddressFromQuickCheckout, onClickAddressChangeBack, setCartAddressState]);

    const displayAddressList = useMemo(
        () => (isCartAddressSelectorDisabled ? addressList : getAllDeliveryValidAddresses(addresses)),
        [addresses, addressList, isCartAddressSelectorDisabled],
    );

    const goToAddAddress = useCallback(
        ({
            selectedAddress,
            addressList,
            source,
        }: {
            selectedAddress?: AddressInfo | null;
            addressList?: AddressInfo[];
            source?: string;
        } = {}) => {
            onClickAddNewAddress(cartTotal, selectedAddress, addressList, source);
            addNewAddress();
        },
        [onClickAddNewAddress, cartTotal, addNewAddress],
    );

    return {
        goToAddAddress,
        onSelectAddress,
        goToSelectAddress,
        onAddressListClose,
        displayAddressList,
        selectedAddress,
    };
};
